import { css } from '@emotion/css';
import { TFunction } from 'i18next';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { actions, DreamErrorType } from '../../../features/dream';
import { getTimeString } from '../../../lib/timeNotation';
import { replaceAllSpaces } from '../../../lib/utils';
import { User } from '../../../models/User';
import { CharacterImage } from '../../shared/CharacterImage';
import { IslandName } from '../../shared/IslandName';
import { DownloadCount } from '../DownloadCount';
import { IslandIcon } from '../IslandIcon';
import { ErrorText } from './ErrorText';

interface CommonDreamHistoryDetailProps {
  isMyIsLandDream: boolean;
  address: string;
  uploadUser: User;
  updatedAt: number;
}

// 自分の島の夢
interface MyIslandDreamHistoryDetailProps extends CommonDreamHistoryDetailProps {
  isMyIsLandDream: true;
  downloadCount: number;
  isSelectedUser: boolean;
}

// 他人の島の夢
interface OthersDreamHistoryDetailProps extends CommonDreamHistoryDetailProps {
  isMyIsLandDream: false;
  visitedAt: number;
  errorType?: DreamErrorType;
}

type DreamHistoryDetailProps = MyIslandDreamHistoryDetailProps | OthersDreamHistoryDetailProps;

export const DreamHistoryDetail: React.FC<DreamHistoryDetailProps> = React.memo(props => {
  const { t, i18n } = useTranslation(['dream', 'translation', 'best_friends']);
  const dispatch = useDispatch();

  const { uploadUser, address, updatedAt, isMyIsLandDream } = props;

  useEffect(() => {
    dispatch(actions.getUserProfile({ userId: uploadUser.id, language: i18n.language }));
  }, [dispatch, uploadUser.id, i18n.language]);

  const dreamAddress = address;
  const username = uploadUser.name;

  // 自分自身の場合は表示する
  const isReported =
    uploadUser.screening === 'ng' &&
    (!props.isMyIsLandDream || (props.isMyIsLandDream && !props.isSelectedUser));

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        color: #4d3906;
        padding: 0 0 12px;
        min-height: 320px;
        h3 {
          svg {
            color: #3f8b64;
            margin-right: 8px;
          }
          font-size: 16px;
          display: flex;
          align-items: center;
        }
        span {
          text-align: center;
        }
        .da-row {
          display: flex;
          flex-direction: column;
          font-size: 14px;
          color: #897a66;
          margin: 12px 0 0;
        }
        .da {
          color: #a364d6;
        }
        .count {
          font-size: 12px;
          margin: 8px 0 0;
          color: #938666;
          em {
            color: #a364d6;
            font-style: normal;
          }
        }
        .player-image {
          width: 72px;
          height: 72px;
          margin: 20px auto 12px;
        }
        .name {
          font-size: 12px;
          margin: 0 0 6px;
        }
        .visited-at,
        .updated-at {
          display: flex;
          flex-direction: column;
          font-size: 12px;
          color: #938666;
          margin: 8px 0;
        }
        @media (min-width: 370px) {
          h3 {
            font-size: 20px;
          }
          .da-row {
            font-size: 16px;
          }
        }
      `}
    >
      <h3>
        <IslandIcon size={40} />
        <div>
          {!isMyIsLandDream && uploadUser.screening === 'ng' ? (
            t('translation:reported_player_island')
          ) : (
            <IslandName
              landName={uploadUser.land.name}
              landDisplayId={uploadUser.land.displayId}
              language={i18n.language}
            />
          )}
        </div>
      </h3>
      <div className="da-row">
        <span>{t('dream_address')}</span>
        <span className="da">{dreamAddress}</span>
      </div>
      {props.isMyIsLandDream && props.downloadCount > 0 && (
        <div className="count">
          <DownloadCount count={props.downloadCount} />
        </div>
      )}
      {!props.isMyIsLandDream && (
        <div className="visited-at">
          <span>{t('dreamed_date')}</span>
          <span>{getDateString(t, i18n.language, new Date(props.visitedAt * 1000))}</span>
        </div>
      )}
      <div className="player-image">
        <CharacterImage
          src={uploadUser.image}
          isPlayer={true}
          isReported={isReported}
          imageAriaLabel={t('best_friends:aria_player_image')}
        />
      </div>
      <div className="name">
        {isReported ? t('translation:reported_player') : replaceAllSpaces(username)}
      </div>

      <div className="updated-at">
        <span>{t('dream_latest_update')}</span>
        <span>{getDateString(t, i18n.language, new Date(updatedAt * 1000))}</span>
      </div>

      {!props.isMyIsLandDream && props.errorType && <ErrorText errorType={props.errorType} />}
    </div>
  );
});

// helper function
function getDateString(t: TFunction, language: string, date: Date) {
  const dateString = t('translation:date_ymd', {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate()
  });
  const timeString = getTimeString(date, language);

  return dateString + ' ' + timeString;
}
